import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getGroup, getGroupMembers, updateGroupMember } from '../lib/firestore/groups';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../styles/EventCohosts.css';

// Separate component for cohosts list to handle async user data loading
const CohostsList = ({ cohosts, onRemove }) => {
  const [cohostsData, setCohostsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCohostsData = async () => {
      const data = await Promise.all(
        cohosts.map(async (cohost) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', cohost.userId));
            const userData = userDoc.exists() ? userDoc.data() : null;
            return {
              ...cohost,
              userData,
              displayName: userData
                ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Cohost'
                : 'Cohost',
            };
          } catch (error) {
            console.error('Error loading cohost data:', error);
            return {
              ...cohost,
              userData: null,
              displayName: 'Cohost',
            };
          }
        })
      );
      setCohostsData(data);
      setLoading(false);
    };

    if (cohosts.length > 0) {
      loadCohostsData();
    } else {
      setLoading(false);
    }
  }, [cohosts]);

  if (loading) {
    return <div className="loading">Loading cohosts...</div>;
  }

  return (
    <div className="cohosts-list">
      {cohostsData.map((cohost) => (
        <div key={cohost.userId} className="cohost-row">
          <div className="cohost-info">
            <div className="cohost-id">{cohost.displayName}</div>
            <div className="cohost-meta">
              {cohost.addedAt 
                ? `Added ${new Date(cohost.addedAt).toLocaleDateString()}`
                : 'Cohost'}
            </div>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onRemove(cohost.userId)}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
};

const GroupCohosts = () => {
  console.log('🔵 [GroupCohosts] ====== COMPONENT MOUNTING ======');
  console.log('🔵 [GroupCohosts] Current URL:', window.location.href);
  console.log('🔵 [GroupCohosts] Current pathname:', window.location.pathname);
  
  const { groupId } = useParams();
  const { user } = useAuthState();
  const navigate = useNavigate();
  
  console.log('🔵 [GroupCohosts] Component rendered:', { groupId, pathname: window.location.pathname, hasUser: !!user, userId: user?.uid });

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [cohosts, setCohosts] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMember, setSelectedMember] = useState('');

  useEffect(() => {
    console.log('🔵 [GroupCohosts] useEffect triggered:', { groupId, hasUser: !!user, pathname: window.location.pathname });
    console.log('🔵 [GroupCohosts] useParams result:', { groupId });
    console.log('🔵 [GroupCohosts] window.location:', window.location.href);
    
    if (groupId && user) {
      console.log('🟢 [GroupCohosts] Loading data for group:', groupId);
      loadData();
    } else if (!groupId) {
      console.error('🔴 [GroupCohosts] Missing groupId from useParams, current path:', window.location.pathname);
      console.error('🔴 [GroupCohosts] This might cause a redirect. Checking if we should redirect...');
      // Don't redirect here - let ProtectedRoute handle it
    }
    // Removed navigate from dependencies and removed redirect - ProtectedRoute handles auth redirects
  }, [groupId, user]);

  const loadData = async () => {
    if (!groupId || !user) return;

    setLoading(true);
    try {
      const groupData = await getGroup(groupId);
      if (!groupData) {
        setError('Group not found');
        setLoading(false);
        return;
      }

      // Check if user is owner
      if (groupData.ownerId !== user.uid) {
        console.log('🔴 [GroupCohosts] User is not owner, redirecting:', {
          groupOwnerId: groupData.ownerId,
          userId: user.uid
        });
        setError('Only the group owner can manage cohosts');
        setLoading(false);
        // Don't redirect here - let user see the error message
        return;
      }

      setGroup(groupData);

      // Load all members
      const membersList = await getGroupMembers(groupId);
      setMembers(membersList);

      // Load user data for all members to display names
      const membersWithUserData = await Promise.all(
        membersList.map(async (member) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', member.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                ...member,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
              };
            }
            return member;
          } catch (err) {
            console.error(`Error loading user data for ${member.userId}:`, err);
            return member;
          }
        })
      );

      // Find cohosts (members with role 'cohost')
      const cohostsList = membersWithUserData.filter(m => m.role === 'cohost');
      setCohosts(cohostsList);

      // Filter out owner and existing cohosts
      const available = membersWithUserData.filter(
        (m) =>
          m.userId !== user.uid &&
          m.role !== 'cohost' &&
          m.role !== 'owner'
      );
      setAvailableMembers(available);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCohost = async () => {
    if (!selectedMember || !user || !groupId) return;

    try {
      console.log('🔵 [GroupCohosts] Adding cohost:', { groupId, userId: selectedMember, currentUser: user.uid });
      // Update member role to cohost
      await updateGroupMember(groupId, selectedMember, { role: 'cohost' });
      console.log('🟢 [GroupCohosts] Cohost added successfully');
      await loadData();
      setSelectedMember('');
      alert('Cohost added successfully!');
    } catch (err) {
      console.error('🔴 [GroupCohosts] Error adding cohost:', err);
      console.error('🔴 [GroupCohosts] Error details:', {
        code: err?.code,
        message: err?.message,
        groupId,
        userId: selectedMember,
        currentUser: user.uid
      });
      setError(err.message || 'Failed to add cohost');
      alert(`Failed to add cohost: ${err.message || 'Unknown error'}`);
    }
  };

  const handleRemoveCohost = async (userId) => {
    if (!confirm('Remove this cohost? They will become a regular member.')) return;

    try {
      console.log('🔵 [GroupCohosts] Removing cohost:', { groupId, userId, currentUser: user.uid });
      // Change role back to member
      await updateGroupMember(groupId, userId, { role: 'member' });
      console.log('🟢 [GroupCohosts] Cohost removed successfully');
      await loadData();
      alert('Cohost removed successfully!');
    } catch (err) {
      console.error('🔴 [GroupCohosts] Error removing cohost:', err);
      console.error('🔴 [GroupCohosts] Error details:', {
        code: err?.code,
        message: err?.message,
        groupId,
        userId,
        currentUser: user.uid
      });
      setError(err.message || 'Failed to remove cohost');
      alert(`Failed to remove cohost: ${err.message || 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="event-cohosts-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="event-cohosts-container">
        <div className="cohosts-header">
          <button className="btn-back" onClick={() => navigate(`/groups/${groupId}`)}>
            ← Back
          </button>
          <h1>Manage Cohosts</h1>
        </div>
        <div className="error-message">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate(`/groups/${groupId}`)}>
          Back to Group
        </button>
      </div>
    );
  }

  return (
    <div className="event-cohosts-container">
      <div className="event-cohosts-card">
        <header className="cohosts-header">
          <button className="btn-back" onClick={() => navigate(`/groups/${groupId}`)}>
            ← Back to Group
          </button>
          <h1>Manage Cohosts</h1>
          <p>{group?.name}</p>
        </header>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

        <div className="cohosts-content">
          {/* Add Cohost Section */}
          {availableMembers.length > 0 && (
            <section className="add-cohost-section">
              <h2>Add Cohost</h2>
              <div className="add-cohost-form">
                <select
                  className="input"
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                >
                  <option value="">Select a member...</option>
                  {availableMembers.map((member) => {
                    const displayName = member.firstName || member.lastName
                      ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                      : member.email || `User ${member.userId.slice(0, 8)}...`;
                    return (
                      <option key={member.userId} value={member.userId}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
                <button
                  className="btn btn-primary"
                  onClick={handleAddCohost}
                  disabled={!selectedMember}
                >
                  Add as Cohost
                </button>
              </div>
            </section>
          )}

          {/* Current Cohosts */}
          <section className="cohosts-list-section">
            <h2>Current Cohosts ({cohosts.length})</h2>
            {cohosts.length === 0 ? (
              <p className="empty-state">No cohosts added yet</p>
            ) : (
              <CohostsList cohosts={cohosts} onRemove={handleRemoveCohost} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default GroupCohosts;

